import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Inbox, ExternalLink, RefreshCw } from 'lucide-react';
import logger from '@/lib/logger';

/**
 * Super-admin-only card that surfaces how many leads are currently parked
 * under the default super admin awaiting manual reassignment.
 *
 * Rationale: since the rejection + SLA breach flow now auto-assigns to the
 * super admin instead of leaving `assigned_to = None`, this widget is the
 * single lens for "leads waiting for me to reassign".
 */
export const ParkedLeadsWidget = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetch = async () => {
        try {
            const r = await apiClient.get('/admin/parked-leads-summary');
            setData(r.data);
        } catch (e) {
            logger.error('parked-leads-summary failed', e);
            setData({ total: 0, by_stage: [], by_previous_team: [], recent: [] });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetch();
        const t = setInterval(fetch, 60000);
        return () => clearInterval(t);
    }, []);

    const handleJump = () => {
        // Deep-link into Leads Pool filtered to the super admin's parked bucket.
        navigate('/leads/pool?owner=me&auto_parked=1');
    };

    if (loading) {
        return (
            <Card className="border-amber-500/30" data-testid="parked-leads-widget">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Parked Under You</CardTitle></CardHeader>
                <CardContent><div className="h-24 animate-pulse bg-muted/40 rounded" /></CardContent>
            </Card>
        );
    }

    const total = data?.total || 0;
    const topTeams = (data?.by_previous_team || []).slice(0, 4);

    return (
        <Card
            className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5"
            data-testid="parked-leads-widget"
        >
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-2">
                    <Inbox className="h-4 w-4 text-amber-500" />
                    <CardTitle className="text-sm text-muted-foreground">
                        Parked Under You
                    </CardTitle>
                </div>
                <button
                    onClick={() => { setRefreshing(true); fetch(); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title="Refresh"
                    data-testid="parked-leads-refresh"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-baseline gap-2">
                    <div
                        className="text-4xl font-bold text-amber-500 tabular-nums"
                        data-testid="parked-leads-total"
                    >
                        {total.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        leads awaiting reassignment
                    </div>
                </div>

                {topTeams.length > 0 && (
                    <div className="space-y-1.5">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            From (top teams)
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {topTeams.map((t, i) => (
                                <Badge
                                    key={`${t.team_id || 'none'}-${i}`}
                                    variant="outline"
                                    className="text-[10px] font-normal"
                                    data-testid={`parked-leads-team-${i}`}
                                >
                                    {t.team_name}
                                    <span className="ml-1 text-amber-500 font-semibold">
                                        {t.count.toLocaleString()}
                                    </span>
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}

                <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 text-xs"
                    onClick={handleJump}
                    disabled={total === 0}
                    data-testid="parked-leads-jump"
                >
                    <ExternalLink className="h-3 w-3 mr-1.5" />
                    Open in Leads Pool
                </Button>
            </CardContent>
        </Card>
    );
};

export default ParkedLeadsWidget;
